import { Component, Injector } from '@angular/core';
import { AppComponentBase } from '@shared/common/app-component-base';
import { ProjectServiceProxy, UploadProjectDocumentInput } from '@shared/service-proxies/service-proxies';
import { firstValueFrom } from 'rxjs';

export interface PendingProjectDoc {
    file: File;
    fileName: string;
    fileSize: number;
    fileType: string;
    icon: string;
}

@Component({
    standalone: false,
    selector: 'app-project-docs-upload',
    templateUrl: './project-docs-upload.component.html',
    styleUrls: ['./project-docs-upload.component.scss']
})
export class ProjectDocsUploadComponent extends AppComponentBase {
    selectedFiles: PendingProjectDoc[] = [];
    readonly maxFileSize = 15 * 1024 * 1024;
    readonly allowedTypes = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'png', 'jpg', 'jpeg', 'gif', 'txt', 'zip', 'csv'];
    readonly fileIcons: Record<string, string> = {
        pdf: 'assets/common/images/timetracking/Attachments/TimeTrackingPdfFileIcon.svg',
        zip: 'assets/common/images/timetracking/Attachments/TimeTrackingZipFileIcon.svg',
        docx: 'assets/common/images/timetracking/Attachments/wordAttachment.svg',
        doc: 'assets/common/images/timetracking/Attachments/wordAttachment.svg',
        png: 'assets/common/images/timetracking/Attachments/ImageAttachment.svg',
        jpg: 'assets/common/images/timetracking/Attachments/ImageAttachment.svg',
        jpeg: 'assets/common/images/timetracking/Attachments/ImageAttachment.svg',
        gif: 'assets/common/images/timetracking/Attachments/ImageAttachment.svg',
        other: 'assets/common/images/timetracking/Attachments/dummyIconAttachment.svg'
    };

    constructor(
        injector: Injector,
        private _projectServiceProxy: ProjectServiceProxy
    ) {
        super(injector);
    }

    onFilesSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (!input.files?.length) {
            return;
        }

        Array.from(input.files).forEach(file => {
            const ext = this.getExtension(file.name);
            if (!this.allowedTypes.includes(ext)) {
                this.notify.warn(this.l('InvalidProjectDocumentType'));
                return;
            }
            if (file.size > this.maxFileSize) {
                this.notify.warn(this.l('ProjectDocumentSizeExceeded'));
                return;
            }
            if (this.selectedFiles.some(x => x.fileName === file.name && x.fileSize === file.size)) {
                return;
            }
            this.selectedFiles.push({
                file,
                fileName: file.name,
                fileSize: file.size,
                fileType: ext,
                icon: this.fileIcons[ext] || this.fileIcons.other
            });
        });
        input.value = '';
    }

    removeFile(index: number): void {
        this.selectedFiles.splice(index, 1);
    }

    formatSize(bytes: number): string {
        if (!bytes) {
            return '0 KB';
        }
        if (bytes < 1024 * 1024) {
            return (bytes / 1024).toFixed(1) + ' KB';
        }
        return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    }

    async uploadPendingFiles(projectId: number): Promise<void> {
        if (!projectId || !this.selectedFiles.length) {
            return;
        }

        for (const item of this.selectedFiles) {
            const base64 = await this.readAsBase64(item.file);
            const input = new UploadProjectDocumentInput();
            input.projectId = projectId;
            input.tenantId = this.appSession.tenant.id;
            input.fileName = item.fileName;
            input.fileBase64String = base64;
            input.contentType = item.file.type || 'application/octet-stream';
            input.fileSize = item.fileSize;
            await firstValueFrom(this._projectServiceProxy.uploadProjectDocument(input));
        }
        this.selectedFiles = [];
    }

    private getExtension(fileName: string): string {
        const parts = fileName.toLowerCase().split('.');
        return parts.length > 1 ? parts[parts.length - 1] : '';
    }

    private readAsBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const result = reader.result as string;
                const commaIndex = result.indexOf(',');
                resolve(commaIndex >= 0 ? result.substring(commaIndex + 1) : result);
            };
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(file);
        });
    }
}
